import { useState } from "react";
import { CheckCircle2, XCircle, Copy, Download, Link2, Clock, Fingerprint, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { JsonViewer } from "./JsonViewer";
import { isSuccessfulMigration, getTechnicalHint } from "@/lib/api";

const StatusBadge = ({ result }) => {
  const success = isSuccessfulMigration(result);
  const status = result.http_status;
  const cls = success
    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    : "bg-red-500/10 text-red-400 border-red-500/20";
  return (
    <span
      data-testid="http-status-badge"
      className={`inline-flex items-center gap-1.5 border ${cls} px-2.5 py-1 rounded-full text-xs font-mono font-semibold`}
    >
      {success ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      HTTP {status || "—"}
    </span>
  );
};

const MetaRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-2 text-xs">
    <Icon className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0" />
    <span className="text-zinc-500 shrink-0">{label}</span>
    <span data-testid={testId} className="font-mono text-zinc-300 break-all">
      {value || "—"}
    </span>
  </div>
);

export const ResponsePanel = ({ result }) => {
  const [showHeaders, setShowHeaders] = useState(false);
  const success = isSuccessfulMigration(result);
  const hint = getTechnicalHint(result);

  const copyResponse = async () => {
    await navigator.clipboard.writeText(JSON.stringify(result.response_body, null, 2));
    toast.success("Exact Meta response copied to clipboard");
  };

  const downloadJson = () => {
    const payload = {
      timestamp: result.request_timestamp,
      source_waba_id: result.source_waba_id,
      destination_waba_id: result.destination_waba_id,
      http_status: result.http_status,
      meta_response: result.response_body,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `meta-migration-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Response JSON downloaded");
  };

  return (
    <div data-testid="response-panel" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">Meta Response</h3>
          <StatusBadge result={result} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="copy-response-button"
            onClick={copyResponse}
            variant="outline"
            size="sm"
            className="h-8 border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white"
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Response
          </Button>
          <Button
            data-testid="download-response-button"
            onClick={downloadJson}
            variant="outline"
            size="sm"
            className="h-8 border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download JSON
          </Button>
        </div>
      </div>

      {/* Request metadata (no access token exposed) */}
      <div className="grid gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
        <MetaRow icon={Link2} label="Request URL:" value={result.request_url} testId="request-url" />
        <MetaRow icon={Clock} label="Timestamp:" value={result.request_timestamp} testId="request-timestamp" />
        <MetaRow icon={Fingerprint} label="fbtrace_id:" value={result.fbtrace_id} testId="fbtrace-id" />
      </div>

      {/* Response headers (collapsible) */}
      <div className="rounded-md border border-zinc-800 bg-zinc-950/60">
        <button
          data-testid="toggle-headers-button"
          onClick={() => setShowHeaders((s) => !s)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <span>Response Headers</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showHeaders ? "rotate-180" : ""}`} />
        </button>
        {showHeaders && (
          <div className="border-t border-zinc-800 p-3">
            <JsonViewer data={result.response_headers} testId="response-headers-viewer" />
          </div>
        )}
      </div>

      {/* Response body — the exact raw Meta response */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Response Body</p>
        <JsonViewer data={result.response_body} testId="response-json-viewer" />
      </div>

      {/* Optional technical hint below the raw response */}
      {hint && (
        <div
          data-testid="technical-hint"
          className={`rounded-md border p-3 text-xs leading-relaxed ${
            success
              ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-300/90"
              : "border-amber-500/20 bg-amber-500/5 text-amber-300/90"
          }`}
        >
          <span className="font-semibold">Note: </span>
          {hint}
        </div>
      )}
    </div>
  );
};
